<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, useTemplateRef } from 'vue'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'
import { subscriptionModule } from '@/store/subscription/module'
import { screenModule } from '@/store/screen/module'
import { instructorModule } from '@/store/instructor/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { setCookie } from '@/utils'
import type { ComponentPublicInstance } from 'vue'

const {
    Icon,
    Button: PocketButton,
} = UIKit

const emit = defineEmits<{
    clickDropdownOption: [option: string]
    close: []
    clickShowUpgrade: []
}>()

const focusRefIndex = ref<number | null>(null)

const userMenuItemSettings = useTemplateRef<HTMLElement>('user-menu-item-settings')
const userMenuItemDarkMode = useTemplateRef<HTMLElement>('user-menu-item-dark-mode')
const userMenuItemTeach = useTemplateRef<ComponentPublicInstance | HTMLElement>('user-menu-item-teach')
const userMenuItemSignOut = useTemplateRef<ComponentPublicInstance | HTMLElement>('user-menu-item-sign-out')
const userMenuItemUpgrade = useTemplateRef<ComponentPublicInstance | HTMLElement>('user-menu-item-upgrade')

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const initial = computed(() => {
    return userModule.getters.userInitial()
})

const fullName = computed(() => {
    return userModule.getters.userFullName()
})

const hasActiveSubscription = computed(() => {
    return !!subscriptionModule.getters.getSubscriptionForExamId()
})

const hasInstructorDashboardAccess = computed(() => {
    return instructorModule.getters.getCurrentUserOrgInstructors().length > 0
})

const menuRefs = computed(() => {
    const getElement = (element: ComponentPublicInstance | HTMLElement | null | undefined): HTMLElement | null => {
        if (!element) return null
        if ('$el' in element) {
            return element.$el
        }
        return element
    }

    const refs: (HTMLElement | null)[] = [
        userMenuItemSettings.value ?? null,
        userMenuItemDarkMode.value ?? null,
        getElement(userMenuItemTeach.value),
        getElement(userMenuItemSignOut.value),
        getElement(userMenuItemUpgrade.value),
    ]
    return refs.filter((el): el is HTMLElement => el !== null)
})

const nextRefIndex = computed(() => {
    if (focusRefIndex.value === null || focusRefIndex.value === menuRefs.value.length - 1) {
        return 0
    }
    return focusRefIndex.value + 1
})

const previousRefIndex = computed(() => {
    if (!focusRefIndex.value) {
        return menuRefs.value.length - 1
    }
    return focusRefIndex.value - 1
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        instructorModule.actions.fetchCurrentUserOrgInstructors(),
    ])

    // close user menu on `esc` press
    window.addEventListener('keydown', keydownListener, true)
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', keydownListener, true)
})

const stringHasSpaces = (val: string) => {
    return val.includes(' ')
}

const keydownListener = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
        e.stopPropagation()
        emit('close')
    }
    if (e.key === 'ArrowDown') {
        e.preventDefault()
        menuRefs.value[nextRefIndex.value]?.focus()
        focusRefIndex.value = nextRefIndex.value
    }
    if (e.key === 'ArrowUp') {
        e.preventDefault()
        menuRefs.value[previousRefIndex.value]?.focus()
        focusRefIndex.value = previousRefIndex.value
    }
}

const clickDarkMode = () => {
    userModule.actions.toggleIsDarkMode()
}

const clickSignOut = () => {
    emit('clickDropdownOption', 'signOut')
}

const clickInstructorDashboard = () => {
    // On web, we want to open Instructor Dashboard in the same tab
    const teachURL = import.meta.env.VUE_APP_TEACH_URL
    const sessionCookieName = import.meta.env.VUE_APP_SESSION_COOKIE_NAME
    const sessionToken = userModule.getters.getSessionToken()
    if (teachURL && sessionToken && sessionCookieName) {
        setCookie({ name: sessionCookieName, value: sessionToken })
        window.location.href = teachURL
    }
}

const emitClose = () => {
    emit('close')
}

const emitClickShowUpgrade = () => {
    emit('clickShowUpgrade')
}
</script>

<template>
    <div>
        <ul class="user-menu-dropdown">
            <li
                v-dark
                role="img"
                :aria-label="fullName"
                class="user-menu-dropdown__item"
            >
                <div aria-hidden="true" class="user-menu-dropdown__top">
                    <div class="user-menu-dropdown__initial">
                        <span>{{ initial }}</span>
                    </div>
                    <div
                        v-if="fullName"
                        v-dark
                        class="user-menu-dropdown__name"
                        :class="{ 'user-menu-dropdown__name--force-break': !stringHasSpaces(fullName) }"
                    >
                        {{ fullName }}
                    </div>
                </div>
            </li>
            <li v-dark class="user-menu-dropdown__item user-menu-dropdown__settings">
                <RouterLink
                    custom
                    :to="{ name: 'account' }"
                    v-slot="{ navigate }"
                    :key="String(isDarkMode)"
                >
                    <a
                        v-dark
                        tabindex="0"
                        ref="user-menu-item-settings"
                        class="user-menu-dropdown__settings-link"
                        @click="emitClose(); navigate($event)"
                        @keydown.enter="emitClose(); navigate()"
                    >
                        Settings
                    </a>
                </RouterLink>
            </li>
            <li
                ref="user-menu-item-dark-mode"
                v-dark
                tabindex="0"
                class="user-menu-dropdown__item user-menu-dropdown__dark-mode"
                role="switch"
                :aria-checked="isDarkMode"
                @click="clickDarkMode"
                @keydown.enter="clickDarkMode"
                @mousedown.prevent
            >
                <div v-dark class="user-menu-dropdown__dark-mode-inner">
                    Dark Mode <span v-dark class="user-menu-dropdown__dark-mode-icon"><Icon type="moon" /></span>
                </div>
            </li>
            <li
                v-if="hasInstructorDashboardAccess"
                ref="user-menu-item-teach"
                v-dark
                tabindex="0"
                class="user-menu-dropdown__item user-menu-dropdown__teach"
                role="link"
                @click="clickInstructorDashboard"
                @keydown.enter="clickInstructorDashboard"
                @mousedown.prevent
            >
                Go to Instructor Dashboard
            </li>
            <li
                ref="user-menu-item-sign-out"
                v-dark
                tabindex="0"
                class="user-menu-dropdown__item user-menu-dropdown__sign-out"
                role="link"
                @click="clickSignOut"
                @keydown.enter="clickSignOut"
                @mousedown.prevent
            >
                Sign Out
            </li>
            <li
                v-if="!hasActiveSubscription && currentExamMetadata && breakpoint === 'brown-bear'"
                v-dark
                class="user-menu-dropdown__item user-menu-dropdown__upgrade"
            >
                <PocketButton
                    class="user-menu-dropdown__upgrade-btn"
                    ref="user-menu-item-upgrade"
                    :is-dark-mode="isDarkMode"
                    @click="emitClickShowUpgrade"
                >
                    Upgrade to Premium
                </PocketButton>
            </li>
        </ul>
    </div>
</template>

<style lang="scss" scoped>
.user-menu-dropdown {
    list-style: none;
    margin: 0;
    padding: 0;
    color: $brand-black;

    &__item {
        margin: 0;
        border-bottom: 1px solid $gray-divider;
        font-size: 14px;
        line-height: 17px;
        padding: 0 15px;
        min-height: 40px;
        display: flex;
        align-items: center;

        &--dark {
            border-bottom-color: rgba($fog, 0.25);
            color: $gray-background;
        }

        &:last-child {
            border-bottom: none;
            border-radius: 0 0 8px 8px;
        }
    }

    &__settings {
        padding: 0 !important;
    }

    &__settings-link {
        color: $brand-black;
        text-decoration: none;
        display: block;
        padding: 0 15px;
        height: 40px;
        line-height: 40px;
        width: 100%;

        &--dark {
            color: $gray-background;
        }
    }

    &__top {
        margin: 0 auto;
    }

    &__initial {
        width: 50px;
        height: 50px;
        border-radius: 50px;
        color: $flat-brown;
        margin: 23px auto 12px;
        background: linear-gradient(216.65deg, $beach-sand 0%, $cream-puff 100%);
        line-height: 1px;
        font-size: 24px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        text-transform: uppercase;
    }

    &__name {
        font-size: 16px;
        line-height: 22px;
        font-weight: 600;
        text-align: center;
        margin-bottom: 18px;
        color: #6d4705;

        &--force-break {
            word-break: break-all;
        }

        &--dark {
            color: $white;
        }
    }

    &__settings-link,
    &__dark-mode,
    &__teach,
    &__sign-out {
        cursor: pointer;
        outline: none;
        position: relative;

        &:hover {
            background-color: $gray-background;
        }

        &:focus::before {
            content: '';
            position: absolute;
            left: 0;
            top: 0;
            width: calc(100% - 2px);
            height: calc(100% - 2px);
            border: 1px solid $brand-blue;
            border-radius: 6px;
        }

        &--dark {
            &:hover {
                background-color: $brand-black;
            }

            &:focus::before {
                border-color: $banana-bread;
            }
        }
    }

    &__dark-mode-icon {
        display: inline-block;
        margin-left: 6px;
        background: rgba($brand-black, 0.12);
        border-radius: 4px;
        width: 23px;
        height: 23px;
        vertical-align: middle;

        &--dark {
            background: transparent;

            svg {
                color: $gray-background !important;
            }
        }

        svg {
            display: block;
            color: $brand-black;
            margin-left: 4px;
            margin-top: 3px;
        }
    }

    &__dark-mode-inner {
        height: 26px;
        position: relative;
        padding: 0 0 0 7px;
        line-height: 26px;
        z-index: 1;
        left: -7px;

        &--dark::before {
            content: '';
            position: absolute;
            background: $white;
            opacity: 0.17;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            border-radius: 6px;
        }
    }

    .user-menu-dropdown__upgrade {
        padding: 8px;
    }

    &__upgrade-btn {
        width: 100%
    }
}
</style>
